import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import ControlledTable from 'components/shared/table/ControlledTable';
import useIsMobile from 'hooks/useIsMobile';
import usePagination from 'hooks/usePagination';
import Summarization from 'models/Summarization';
import { indexSummarizations, summarizationQueryKeys } from 'services/api/summarizations';
import { serializeSort, useSummarizationsActions, useSummarizationsStore } from 'stores/summarizationsStore';

import SummarizationsFilterBar from './SummarizationsFilterBar';
import summarizationsTableColumns from './summarizationsTableColumns';

const SummarizationsListPage = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const filters = useSummarizationsStore((state) => state.filters);
  const sort = useSummarizationsStore((state) => state.sort);
  const search = useSummarizationsStore((state) => state.search);
  const { setSort, setSearch, setFilters } = useSummarizationsActions();
  const pagination = usePagination();

  useEffect(() => () => setSearch(''), [setSearch]);

  const onClickSummarization = useCallback((id: string) => {
    navigate(`/summarizations/${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => summarizationsTableColumns({ onClickSummarization }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
      filters,
      include: 'attachmentCount',
    }),
    [search, filters, pagination.paging, sort]
  );

  const { data: summarizations, isLoading } = useQuery({
    queryKey: summarizationQueryKeys.index({ ...queryParams }),
    queryFn: ({ signal }) => indexSummarizations(queryParams, signal),
  });

  const handleApplyFilters = useCallback(
    ({ search, ...filters }) => {
      setFilters(filters);
      setSearch(search);

      if (search) {
        setSort([{ id: 'search', desc: true }]);
      } else if (sort.some(({ id }) => id === 'search')) {
        setSort([{ id: 'createdAt', desc: true }]);
      }
    },
    [setFilters, setSort, setSearch, sort]
  );

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  return (
    <Page>
      <SummarizationsFilterBar onApplyFilters={handleApplyFilters} />
      <Page.Content>
        <ControlledTable<Summarization>
          data={(summarizations?.data ?? []) as Summarization[]}
          filters={filters}
          columns={columns}
          loading={isLoading}
          pageCount={summarizations?.meta.totalPages ?? 1}
          pagination={pagination}
          sorting={{ sortBy: sort, setSortBy: setSort }}
          state={{
            columnVisibility: {
              sourcesCount: !isMobile,
              createdAt: !isMobile,
            },
          }}
        />
      </Page.Content>
    </Page>
  );
};

export default SummarizationsListPage;
